// TODO: Use functional component syntax so we can eventually re-enable this.
/* eslint-disable max-classes-per-file */

import React from 'react';
import { string, bool, shape, func, arrayOf } from 'prop-types';
import { uniqueId } from 'lodash';
import ClassNames from 'classnames';

import BaseComponent from '../.internal/BaseComponent';
import Icon from '../icon/Icon';
import Tooltip from '../tooltip/Tooltip';

import './Upload.scss';

export default class Upload extends BaseComponent {
  constructor(props) {
    super(props);
    this.inputRef = React.createRef();
  }

  handleChange = async event => {
    const currentFiles = this.props.files;
    const addedFiles = await this.processFiles(event.target.files);
    const updatedFiles = this.props.multiple
      ? [...currentFiles, ...addedFiles]
      : addedFiles;

    this.props.onChange(updatedFiles);

    // File inputs only emit a change event when they detect that newly selected
    // files do not match a file that was previously selected. If, for example,
    // a user adds a file, removes it, then attempts to add it again, this
    // change handler will not be re-invoked, meaning the file won't be
    // re-processed after being removed. To prevent this, we "clear the input's
    // internal file memory" by setting its value to an empty string.
    this.inputRef.current.value = '';
  };

  processFiles = async files => {
    const filesArray = Array.from(files);
    const processingResults = await Promise.allSettled(
      filesArray.map(file => this.props.onAdd(file))
    );

    const processedFiles = processingResults.map((result, i) => {
      const file = filesArray[i];
      const defaultId = uniqueId('file_');

      if (result.status === 'rejected') {
        return {
          id: defaultId,
          error: result.reason,
          name: file.name,
          url: null
        };
      }

      const {
        id = defaultId,
        name = file.name,
        url = URL.createObjectURL(file)
      } = result.value || {};

      return { id, blob: file, error: null, name, url };
    });

    return processedFiles;
  };

  removeFile = async fileToRemove => {
    await this.props.onRemove(fileToRemove);

    const remainingFiles = this.props.files.filter(
      file => file !== fileToRemove
    );

    this.props.onChange(remainingFiles);
  };

  renderFileItem(file) {
    return (
      <li
        className={ClassNames(
          'upload__file-item',
          file.error && 'upload__file-item_error'
        )}
        key={file.id}
      >
        <a
          className="upload__file-item-link"
          href={file.url}
          rel="noopener noreferrer"
          target="_blank"
        >
          <Icon className="upload__file-item-icon" icon="paperclip" size="sm" />
          <div className="upload__file-item-name">{file.name}</div>
        </a>
        {!this.props.disableDelete && (
          <button
            type="button"
            className="upload__file-item-delete"
            onClick={() => this.removeFile(file)}
          >
            <Icon className="upload__file-item-icon" icon="trash" size="sm" />
          </button>
        )}
      </li>
    );
  }

  render() {
    const {
      multiple,
      accept,
      showFileList,
      style,
      id,
      className,
      disabled,
      autoFocus,
      onFocus,
      onBlur,
      files
    } = this.props;

    const classes = ['upload', className, disabled && 'disabled'];
    const classNames = ClassNames(classes);

    return (
      <div className={classNames}>
        <div className="upload__button-container">
          <label
            aria-disabled={disabled}
            className="upload__button"
            htmlFor={id}
          >
            <input
              accept={accept}
              // TODO: Reconsider use of the autoFocus prop.
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={autoFocus}
              className="upload__file-input"
              disabled={disabled}
              id={id}
              multiple={multiple}
              onBlur={onBlur}
              onChange={this.handleChange}
              onFocus={onFocus}
              ref={this.inputRef}
              style={style}
              type="file"
            />
            Select {multiple ? 'files' : 'file'}
          </label>
        </div>
        {showFileList && (
          <ul className="upload__file-list">
            {files.map(file =>
              file.error ? (
                <Tooltip content={file.error.message} invert position="top">
                  {this.renderFileItem(file)}
                </Tooltip>
              ) : (
                this.renderFileItem(file)
              )
            )}
          </ul>
        )}
      </div>
    );
  }
}

Upload.propTypes = {
  /**
   * Comma-separated list of [unique file type specifiers](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/file#unique_file_type_specifiers).
   */
  accept: string,
  autoFocus: bool,
  className: string,
  disabled: bool,
  /**
   * Boolean parameter that, when set to true, disables the delete icon,
   * preventing users from deleting the uploaded file.
   * When set to false or omitted, the delete icon remains enabled,
   * allowing users to remove files from the upload list.
   */
  disableDelete: bool,
  files: arrayOf(
    shape({
      id: string,
      name: string.isRequired,
      url: string
    }).isRequired
  ).isRequired,
  id: string,
  multiple: bool,
  /**
   * Callback that will be invoked with a [File
   * object](https://developer.mozilla.org/en-US/docs/Web/API/File) of each
   * newly selected file. If this function returns a promise, the file won't
   * appear in the file list until the promise settles.
   *
   * If it rejects, the file will appear in the file list as having encountered
   * an error and hovering over it will show a tooltip with the message of the
   * error thrown.
   */
  onAdd: func,
  onBlur: func,
  /**
   * Callback that will be invoked with the complete list of files selected for
   * upload each time the list changes.
   */
  onChange: func.isRequired,
  onFocus: func,
  /**
   * Callback that will be invoked with each removed file. If this function
   * returns a promise, the file won't disappear from the file list until the
   * promise resolves.
   *
   * If the promise rejects, the file will remain in the file list.
   */
  onRemove: func,
  showFileList: bool,
  style: shape({})
};

Upload.defaultProps = {
  accept: '*',
  autoFocus: false,
  className: undefined,
  disabled: false,
  disableDelete: false,
  files: [],
  id: undefined,
  multiple: false,
  onAdd: () => {},
  onRemove: () => {},
  showFileList: true,
  style: {}
};

// The file item UI is tightly coupled with a number of attributes it needs to
// render correctly. Because, for asynchronous file uploads (and  when loading
// already uploaded files asynchronously), that data comes from the user, we
// expose a class for them to instantiate that data in a format we can expect.
Upload.File = class File {
  constructor({ id, name, url }) {
    this.id = id;
    this.name = name;
    this.url = url;
  }
};
